import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiPrinter, FiFile, FiX, FiRefreshCw } from 'react-icons/fi';
import { useContextoSesion } from '../miscelaneo/ContextoSesion';
import instanciaAxios from '../miscelaneo/instanciaAxios';
import qzTrayService from '../services/qzTrayService';
import ModalPDFBase64 from '../componentes/util/ModalPDFBase64';

const ContextoImpresion = createContext();

export const useImpresion = () => useContext(ContextoImpresion);

const FORMATO_CONFIG_MAP = {
  factura:       'formato_pdf_factura',
  compra:        'formato_pdf_compra',
  gasto_menor:   'formato_pdf_gasto_menor',
  pago_exterior: 'formato_pdf_pago_exterior',
  nota_credito:  'formato_pdf_nota_credito',
  nota_debito:   'formato_pdf_nota_debito',
  recibo_pago:   'formato_pdf_recibo_pago',
  cotizacion:    'formato_pdf_cotizacion',
};

const ENDPOINT_MAP = {
  factura:          (id) => `/factura/${id}/pdf`,
  compra:           (id) => `/compra/${id}/pdf`,
  gasto_menor:      (id) => `/egreso/${id}/pdf`,
  pago_exterior:    (id) => `/pago-exterior/${id}/pdf`,
  nota_credito:     (id) => `/nota-credito-factura/${id}/pdf`,
  nota_debito:      (id) => `/nota-debito-factura/${id}/pdf`,
  recibo_pago:      (id) => `/pago-factura/${id}/recibo`,
  cierre_caja:      (id) => `/sesion-caja/${id}/pdf`,
  retiro_caja:      (id) => `/retiro-caja/${id}/recibo`,
  cotizacion:       (id) => `/cotizacion/${id}/pdf`,
};

export const ContextoImpresionProvider = ({ children }) => {
  const { sesionCaja, usuario } = useContextoSesion();

  const [qzConectado, setQzConectado] = useState(false);
  const [qzConectando, setQzConectando] = useState(false);
  const [impresoras, setImpresoras] = useState([]);

  // Modal fallback state
  const [modalImpresion, setModalImpresion] = useState(null);
  const [impresoraSeleccionada, setImpresoraSeleccionada] = useState('');
  const [modalCargando, setModalCargando] = useState(false);
  const [modalPdfBase64, setModalPdfBase64] = useState(null);

  const impresoraTermica = sesionCaja?.nombre_impresora_termica || null;
  const anchoPapel = sesionCaja?.ancho_papel_termica || '80mm';
  const tieneCajaAbierta = !!sesionCaja;

  // ── Connect on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    // Called immediately when QZ Tray WebSocket closes (no 15s lag).
    const handleDesconexion = () => {
      if (!cancelled) {
        console.warn('[Impresion] QZ Tray desconectado');
        setQzConectado(false);
      }
    };

    const handleReconexion = () => {
      if (!cancelled) {
        setQzConectado(true);
        qzTrayService.obtenerImpresoras().then(p => {
          if (!cancelled) setImpresoras(p);
        });
      }
    };

    const init = async () => {
      // Register the immediate disconnect callback before attempting to connect
      qzTrayService.registrarCallbackDesconexion(handleDesconexion);

      setQzConectando(true);
      const ok = await qzTrayService.conectar({ retries: 1 });
      if (cancelled) return;
      setQzConectado(ok);
      setQzConectando(false);

      if (ok) {
        const printers = await qzTrayService.obtenerImpresoras();
        if (!cancelled) setImpresoras(printers);
      }

      if (!cancelled) {
        qzTrayService.iniciarAutoReconexion(handleReconexion, {
          onDesconexion: handleDesconexion,
        });
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Refresh printer list ──────────────────────────────────────────────────
  const refrescarImpresoras = useCallback(async () => {
    if (!qzTrayService.estaConectado()) {
      const ok = await qzTrayService.conectar({ retries: 0 });
      setQzConectado(ok);
      if (!ok) return [];
    }
    const printers = await qzTrayService.obtenerImpresoras();
    setImpresoras(printers);
    return printers;
  }, []);

  const _resolverFormato = useCallback((formatoExplicito, tipoDocumento) => {
    if (formatoExplicito && formatoExplicito !== 'carta') return formatoExplicito;
    if (!tipoDocumento) return formatoExplicito || 'carta';
    const configKey = FORMATO_CONFIG_MAP[tipoDocumento];
    if (configKey && usuario?.configuracionSistema?.[configKey]) {
      return usuario.configuracionSistema[configKey];
    }
    return formatoExplicito || 'carta';
  }, [usuario]);

  // ── Main print function with fallback ─────────────────────────────────────
  const imprimirDocumento = useCallback(async ({
    idDocumento,
    tipoDocumento,
    formato: formatoExplicito,
    pdfBase64 = null
  }) => {
    const formato = _resolverFormato(formatoExplicito, tipoDocumento);
    const esFormatoContinuo = formato === 'continuo';
    const tieneImpresora = !!impresoraTermica;
    let pdfBase64Resuelto = pdfBase64;
    const logRoute = (route, extra = {}) => {
      console.log('[Impresion][Route]', {
        route,
        tipoDocumento,
        idDocumento,
        formato_resuelto: formato,
        esFormatoContinuo,
        qzActivo,
        tieneImpresora,
        tieneCajaAbierta,
        ...extra
      });
    };

    // ── Pre-print QZ health check ────────────────────────────────────────
    // If QZ reports inactive but we have a printer configured, try a quick
    // reconnect before giving up on QZ (covers the 0-15s gap between a
    // WebSocket drop and the auto-reconnect timer firing).
    // Stale sockets (sleep/wake) are handled by the background timer and by
    // the retry logic inside imprimirRaw/imprimirPDF -- no blocking heartbeat
    // is needed here.
    let qzActivo = qzTrayService.estaConectado();
    if (!qzActivo && tieneImpresora) {
      console.log('[Impresion] QZ inactivo — intentando reconexion rapida');
      qzActivo = await qzTrayService.conectar({ retries: 0 });
      if (qzActivo) {
        setQzConectado(true);
      }
    }

    console.log('[Impresion] imprimirDocumento:', {
      tipoDocumento, idDocumento,
      formatoResuelto: formato, esFormatoContinuo,
      qzActivo, tieneImpresora, tieneCajaAbierta, impresoraTermica,
      tienePdfBase64: !!pdfBase64
    });

    // En continuo, si no hay caja/impresora o QZ no esta activo, mostrar
    // modal de opciones para que el usuario elija ticket/PDF.
    const requiereModalOpcionesContinuo = Boolean(
      esFormatoContinuo &&
      tipoDocumento &&
      idDocumento &&
      (!tieneCajaAbierta || !tieneImpresora || !qzActivo)
    );
    if (requiereModalOpcionesContinuo) {
      const motivo = !tieneCajaAbierta
        ? 'sin_caja'
        : !tieneImpresora
          ? 'sin_impresora'
          : 'qz_inactivo';
      logRoute('modal_options', { motivo });
      setModalImpresion({ idDocumento, tipoDocumento, formato });
      return 'modal';
    }

    // ── Level 1: ESC/POS raw via QZ Tray ────────────────────────────────
    if (esFormatoContinuo && qzActivo && tieneImpresora && tipoDocumento) {
      try {
        const endpointFn = ENDPOINT_MAP[tipoDocumento];
        if (endpointFn) {
          const resp = await instanciaAxios.get(endpointFn(idDocumento), {
            params: { output: 'escpos', formato, ancho_papel: anchoPapel }
          });
          const escposData = resp.data?.data?.pdf_base64 || resp.data?.pdf_base64;
          if (escposData) {
            const ok = await qzTrayService.imprimirRaw(escposData, impresoraTermica);
            if (ok) {
              logRoute('qz_raw');
              toast.success('Impreso correctamente');
              window.dispatchEvent(new CustomEvent('omnierp:print-done'));
              return 'qz';
            }
            console.warn('[Impresion] Level 1 (ESC/POS raw) fallido — pasando a Level 2');
          } else {
            console.warn('[Impresion] Level 1: servidor no retorno datos ESC/POS — pasando a Level 2');
          }
        } else {
          console.warn('[Impresion] Level 1: tipoDocumento sin endpoint ESC/POS:', tipoDocumento, '— pasando a Level 2');
        }
      } catch (err) {
        console.warn('[Impresion] Level 1 (ESC/POS) excepcion — pasando a Level 2:', err?.message);
      }
    } else {
      console.log('[Impresion] Level 1 omitido —', !esFormatoContinuo ? 'formato no continuo' : !qzActivo ? 'QZ inactivo' : !tieneImpresora ? 'sin impresora configurada' : 'sin tipoDocumento');
    }

    // ── Fetch fallback PDF (deterministic) ──────────────────────────────
    if (!pdfBase64Resuelto && tipoDocumento && idDocumento) {
      try {
        const endpointFn = ENDPOINT_MAP[tipoDocumento];
        if (endpointFn) {
          const resp = await instanciaAxios.get(endpointFn(idDocumento), {
            params: { formato }
          });
          pdfBase64Resuelto = resp.data?.data?.pdf_base64 || resp.data?.pdf_base64 || null;
          console.log('[Impresion] PDF fallback obtenido desde API:', {
            tipoDocumento,
            idDocumento,
            formato,
            bytesBase64: pdfBase64Resuelto ? pdfBase64Resuelto.length : 0
          });
        }
      } catch (err) {
        console.warn('[Impresion] No se pudo obtener PDF fallback desde API:', err?.message);
      }
    }

    // ── Level 2: PDF via QZ Tray (continuous format only) ───────────────
    if (esFormatoContinuo && qzActivo && tieneImpresora && pdfBase64Resuelto) {
      try {
        const ok = await qzTrayService.imprimirPDF(pdfBase64Resuelto, impresoraTermica);
        if (ok) {
          logRoute('qz_pdf');
          toast.success('Impreso correctamente');
          window.dispatchEvent(new CustomEvent('omnierp:print-done'));
          return 'qz';
        }
        console.warn('[Impresion] Level 2 (PDF via QZ) fallido — pasando a Level 3 (modal PDF)');
      } catch (err) {
        console.warn('[Impresion] Level 2 (PDF via QZ) excepcion — pasando a Level 3:', err?.message);
      }
    } else if (esFormatoContinuo && tieneImpresora) {
      console.log('[Impresion] Level 2 omitido —', !qzActivo ? 'QZ inactivo' : 'sin pdfBase64 disponible tras fallback');
    }

    // ── Level 3: show PDF in modal for print/download ─────────────────
    if (pdfBase64Resuelto) {
      logRoute('modal_pdf', { fuente: pdfBase64 ? 'payload' : 'api_fallback' });
      console.log('[Impresion] Level 3: mostrando modal PDF (browser)');
      setModalPdfBase64(pdfBase64Resuelto);
      return 'browser';
    }

    // ── Level 4: Show modal with print options ──────────────────────────
    if (tipoDocumento && idDocumento) {
      logRoute('modal_options', { motivo: 'sin_datos_pdf' });
      console.log('[Impresion] Level 4: mostrando modal de opciones de impresion');
      setModalImpresion({ idDocumento, tipoDocumento, formato });
      return 'modal';
    }

    logRoute('none', { motivo: 'sin_tipo_o_id' });
    toast.warning('No se pudo imprimir. Verifique la configuracion de impresion.');
    return null;
  }, [impresoraTermica, anchoPapel, _resolverFormato, tieneCajaAbierta]);

  // ── Modal handlers ────────────────────────────────────────────────────────

  const _cerrarModalImpresion = useCallback(() => {
    setModalImpresion(null);
    setImpresoraSeleccionada('');
    setModalCargando(false);
  }, []);

  const _cerrarModalPdf = useCallback(() => {
    setModalPdfBase64(null);
    window.dispatchEvent(new CustomEvent('omnierp:print-done'));
  }, []);

  const _imprimirPDFCarta = useCallback(async () => {
    if (!modalImpresion) return;
    const { idDocumento, tipoDocumento } = modalImpresion;
    setModalCargando(true);
    try {
      const endpointFn = ENDPOINT_MAP[tipoDocumento];
      if (!endpointFn) throw new Error('Tipo de documento no soportado');
      const resp = await instanciaAxios.get(endpointFn(idDocumento), {
        params: { formato: 'carta' }
      });
      const pdfData = resp.data?.data?.pdf_base64 || resp.data?.pdf_base64;
      if (pdfData) {
        setModalPdfBase64(pdfData);
        _cerrarModalImpresion();
      } else {
        toast.error('No se recibio el PDF del servidor');
      }
    } catch (err) {
      console.error('[Impresion] Error al obtener PDF:', err?.message);
      toast.error('Error al obtener el documento PDF');
    } finally {
      setModalCargando(false);
    }
  }, [modalImpresion, _cerrarModalImpresion]);

  const _imprimirConImpresora = useCallback(async () => {
    if (!modalImpresion || !impresoraSeleccionada) return;
    const { idDocumento, tipoDocumento } = modalImpresion;
    setModalCargando(true);
    try {
      const endpointFn = ENDPOINT_MAP[tipoDocumento];
      if (!endpointFn) throw new Error('Tipo de documento no soportado');
      const resp = await instanciaAxios.get(endpointFn(idDocumento), {
        params: { output: 'escpos', formato: 'continuo', ancho_papel: '80mm' }
      });
      const escposData = resp.data?.data?.pdf_base64 || resp.data?.pdf_base64;
      if (escposData) {
        const ok = await qzTrayService.imprimirRaw(escposData, impresoraSeleccionada);
        if (ok) {
          toast.success('Impreso correctamente');
          _cerrarModalImpresion();
        } else {
          toast.error('Error al enviar a la impresora');
        }
      } else {
        toast.error('No se recibio datos de impresion del servidor');
      }
    } catch (err) {
      console.error('[Impresion] Error al imprimir con impresora:', err?.message);
      toast.error('Error al imprimir el documento');
    } finally {
      setModalCargando(false);
    }
  }, [modalImpresion, impresoraSeleccionada, _cerrarModalImpresion]);

  const _refrescarImpresorasModal = useCallback(async () => {
    setModalCargando(true);
    try {
      await refrescarImpresoras();
    } finally {
      setModalCargando(false);
    }
  }, [refrescarImpresoras]);

  const value = {
    qzConectado,
    qzConectando,
    impresoras,
    impresoraTermica,
    tieneCajaAbierta,
    imprimirDocumento,
    refrescarImpresoras,
  };

  return (
    <ContextoImpresion.Provider value={value}>
      {children}

      {/* ── Modal de opciones de impresion ──────────────────────────────── */}
      {modalImpresion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4 border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-full">
                  <FiPrinter className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Opciones de Impresion</h3>
                  <p className="text-sm text-gray-500">No hay impresora termica configurada en la sesion actual</p>
                </div>
              </div>
              <button
                onClick={_cerrarModalImpresion}
                disabled={modalCargando}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={18} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">Seleccione como desea imprimir el documento:</p>

            {/* QZ Tray printer selection */}
            {qzConectado && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Impresora de tickets (QZ Tray)</p>
                  <button
                    onClick={_refrescarImpresorasModal}
                    disabled={modalCargando}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                    title="Refrescar impresoras"
                  >
                    <FiRefreshCw size={14} className={modalCargando ? 'animate-spin' : ''} />
                  </button>
                </div>
                {impresoras.length > 0 ? (
                  <>
                    <select
                      value={impresoraSeleccionada}
                      onChange={(e) => setImpresoraSeleccionada(e.target.value)}
                      disabled={modalCargando}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                    >
                      <option value="">Seleccionar impresora...</option>
                      {impresoras.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <button
                      onClick={_imprimirConImpresora}
                      disabled={modalCargando || !impresoraSeleccionada}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {modalCargando ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        <FiPrinter size={16} />
                      )}
                      Imprimir Ticket
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-gray-500">No se encontraron impresoras. Presione refrescar.</p>
                )}
              </div>
            )}

            {!qzConectado && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
                <p className="text-xs text-yellow-700">
                  QZ Tray no esta conectado. Para imprimir tickets, inicie QZ Tray y refresque.
                </p>
                <button
                  onClick={_refrescarImpresorasModal}
                  disabled={modalCargando}
                  className="mt-2 text-xs text-yellow-800 underline hover:text-yellow-900"
                >
                  Intentar conectar
                </button>
              </div>
            )}

            {/* PDF Carta + Cancel */}
            <div className="flex gap-3">
              <button
                onClick={_cerrarModalImpresion}
                disabled={modalCargando}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={_imprimirPDFCarta}
                disabled={modalCargando}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-color-primario text-white text-sm font-medium rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {modalCargando ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <FiFile size={16} />
                )}
                Imprimir PDF Carta
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal de vista previa PDF (Level 3 fallback) ─────────────── */}
      <ModalPDFBase64
        mostrar={!!modalPdfBase64}
        onClose={_cerrarModalPdf}
        pdfBase64Directo={modalPdfBase64}
        tituloDocumento="Documento"
      />
    </ContextoImpresion.Provider>
  );
};

export default ContextoImpresion;
